/**
 * 消息端到端加密（客户端 <-> 服务端），基于浏览器原生 Web Crypto API。
 * im-web 只运行在真实浏览器 / Electron（Chromium 内核），crypto.subtle 保证可用，无需额外加密库。
 *
 * 握手流程：
 *   1. GET /system/publicKey 拉取服务端 RSA 公钥（服务端未启用加密时返回空，直接跳过，走明文兼容旧协议）
 *   2. 本地随机生成一把 AES-256-GCM 会话密钥
 *   3. 用 RSA-OAEP(SHA-256) 公钥加密该会话密钥，POST /system/handshake 上传
 *   4. 之后 encrypt()/decrypt() 对消息 content 字段加解密；未握手成功时原样透传，不影响功能
 */

const RSA_ALGO = { name: 'RSA-OAEP', hash: 'SHA-256' }
const AES_ALGO = { name: 'AES-GCM', length: 256 }
const GCM_IV_BYTES = 12

let aesKey = null // CryptoKey，握手成功后持有
let handshakePromise = null

function base64ToBytes(base64) {
	const binary = atob(base64)
	const bytes = new Uint8Array(binary.length)
	for (let i = 0; i < binary.length; i++) {
		bytes[i] = binary.charCodeAt(i)
	}
	return bytes
}

function bytesToBase64(bytes) {
	let binary = ''
	for (let i = 0; i < bytes.length; i++) {
		binary += String.fromCharCode(bytes[i])
	}
	return btoa(binary)
}

async function importRsaPublicKey(base64Spki) {
	// Java X509EncodedKeySpec 导出的公钥格式即 SPKI DER，与 Web Crypto 的 'spki' 格式一致，无需转换
	const keyData = base64ToBytes(base64Spki).buffer
	return crypto.subtle.importKey('spki', keyData, RSA_ALGO, false, ['encrypt'])
}

/**
 * 执行握手。http 为调用方传入的请求实例（避免与 httpRequest.js 产生循环依赖）。
 * 重复调用是安全的：已握手成功后直接复用，不会重复请求。
 */
function handshake(http) {
	if (handshakePromise) {
		return handshakePromise
	}
	handshakePromise = (async () => {
		try {
			const publicKeyBase64 = await http({ url: '/system/publicKey', method: 'get' })
			if (!publicKeyBase64) {
				console.log('[消息加密] 服务端未启用加密，本次会话走明文')
				return
			}
			const publicKey = await importRsaPublicKey(publicKeyBase64)
			aesKey = await crypto.subtle.generateKey(AES_ALGO, true, ['encrypt', 'decrypt'])
			const rawAesKey = await crypto.subtle.exportKey('raw', aesKey)
			const encryptedAesKey = await crypto.subtle.encrypt(RSA_ALGO, publicKey, rawAesKey)
			await http({
				url: '/system/handshake',
				method: 'post',
				data: { encryptedAesKey: bytesToBase64(new Uint8Array(encryptedAesKey)) }
			})
			console.log('[消息加密] 握手成功')
		} catch (e) {
			console.warn('[消息加密] 握手失败，本次会话走明文', e)
			aesKey = null
		}
	})()
	return handshakePromise
}

function hasSessionKey() {
	return aesKey != null
}

async function encrypt(plainText) {
	if (!hasSessionKey() || plainText == null) {
		return plainText
	}
	const iv = crypto.getRandomValues(new Uint8Array(GCM_IV_BYTES))
	const encoded = new TextEncoder().encode(plainText)
	const cipherBuffer = await crypto.subtle.encrypt({ name: 'AES-GCM', iv }, aesKey, encoded)
	const combined = new Uint8Array(iv.length + cipherBuffer.byteLength)
	combined.set(iv, 0)
	combined.set(new Uint8Array(cipherBuffer), iv.length)
	return bytesToBase64(combined)
}

async function decrypt(cipherTextBase64) {
	if (!hasSessionKey() || cipherTextBase64 == null) {
		return cipherTextBase64
	}
	const combined = base64ToBytes(cipherTextBase64)
	const iv = combined.slice(0, GCM_IV_BYTES)
	const cipherBytes = combined.slice(GCM_IV_BYTES)
	const plainBuffer = await crypto.subtle.decrypt({ name: 'AES-GCM', iv }, aesKey, cipherBytes)
	return new TextDecoder().decode(plainBuffer)
}

/** 退出登录/切换账号时重置，下次登录重新握手 */
function reset() {
	aesKey = null
	handshakePromise = null
}

export default {
	handshake,
	hasSessionKey,
	encrypt,
	decrypt,
	reset
}
